import { WeatherDataTypeEnum, resolveWeatherDataType } from "src/app/device/shared/enums/weatherData-type.enum";
import { WeatherConfig } from "../interfaces/weather-config.interface";

/** Maximum number of data types of the detail view, history charts included. */
export const MAX_WEATHER_DETAIL_TYPES = 10;

/** One data type selection of the weather configuration. */
export interface WeatherTypeSelection {
    /** Types this portal version knows and can show for selection. */
    known: WeatherDataTypeEnum[];
    /**
     * true when the read configuration held DE-Typen this portal version does not
     * know. They are invalid and drop out when saving — the flag only drives the
     * hint that the configuration still contains them.
     */
    hasUnknownTypes: boolean;
}

/** The weather configuration in the form the settings view edits. */
export interface WeatherConfigSelection {
    schemaTypes: WeatherTypeSelection;
    detailTypes: WeatherTypeSelection;
}

/** Empty selection for channels without a weather configuration yet. */
export function emptyWeatherConfigSelection(): WeatherConfigSelection {
    return {
        schemaTypes: { known: [], hasUnknownTypes: false },
        detailTypes: { known: [], hasUnknownTypes: false }
    };
}

/**
 * Turns the configuration of the API into the selection offered for editing and
 * notes whether it contained data types unknown here.
 *
 * A channel that is not configured yet comes back with empty or missing lists
 * and yields an empty selection.
 */
export function toWeatherConfigSelection(config: WeatherConfig | null | undefined): WeatherConfigSelection {
    return {
        schemaTypes: toTypeSelection(config?.schemaTypes),
        detailTypes: toTypeSelection(config?.detailTypes)
    };
}

/**
 * Builds the configuration for the API. Only known data types are written — an
 * unknown DE-Typ is invalid and drops out of the configuration when saving.
 */
export function toWeatherConfig(selection: WeatherConfigSelection): WeatherConfig {
    return {
        schemaTypes: [...selection.schemaTypes.known],
        detailTypes: [...selection.detailTypes.known]
    };
}

/** Collects the known DE-Typen of the API, without duplicates, and flags the rest. */
function toTypeSelection(types: unknown): WeatherTypeSelection {
    const selection: WeatherTypeSelection = { known: [], hasUnknownTypes: false };
    if (!Array.isArray(types)) { return selection; }

    for (const type of types) {
        const knownType = typeof type === 'number' ? resolveWeatherDataType(type) : undefined;
        if (knownType === undefined) {
            selection.hasUnknownTypes = true;
            continue;
        }
        if (!selection.known.includes(knownType)) { selection.known.push(knownType); }
    }
    return selection;
}

